/**
 * Ensure User Bootstrap Service
 *
 * Ensures that OAuth users (Google, Microsoft) have the required Firestore
 * documents created after sign-in. This is idempotent - safe to call on every login.
 *
 * For email/password users, atomicRegistration already handles this.
 * For OAuth users, Firebase Auth creates the user but NOT the Firestore docs.
 *
 * ARCHITECTURE NOTE — Option B: direct client-side write for /users/{uid}.
 * Firestore rules allow create/update on /users/{uid} for the authenticated user
 * (allow create: if signedIn() && request.auth.uid == uid).
 * We use setDoc with merge:true and a strict field allowlist (no isPlatformAdmin, no role).
 *
 * This service:
 * 1. Writes /users/{uid} directly via setDoc (idempotent, merge:true)
 * 2. If user has no team, sets onboardingState so OnboardingGuard routes to create-team
 * 3. If user already has a team, returns it directly
 */

import { doc, getDoc, setDoc, serverTimestamp, collection, writeBatch } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '@/lib/firebase';
import { logger } from '@/lib/logger';
import { defaultTeamOnboarding } from '@/types/onboarding';
import { ENTITLEMENT_SCHEMA_VERSION } from '@/lib/entitlement';
import type { User } from 'firebase/auth';
import { findUserTeamMembership, upsertMembershipIndex } from './membershipIndex.service';

/**
 * Wait for Firebase Auth to finish its initial state resolution.
 * Returns the current user (or null if signed out).
 *
 * This prevents Firestore queries from running before the auth token
 * is propagated, which would cause spurious permission-denied errors.
 */
function awaitAuthReady(): Promise<User | null> {
  return new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      resolve(user);
    });
  });
}

export interface BootstrapResult {
  success: boolean;
  hasTeam: boolean;
  teamId: string | null;
  needsTeamSetup: boolean;
  error?: string;
}

export interface CreateTeamResult {
  success: boolean;
  teamId: string | null;
  error?: string;
  /** True if team already existed (idempotent) */
  alreadyExisted?: boolean;
}

// findUserTeamMembership is imported from membershipIndex.service.ts
// It uses the users/{uid}/memberships index with collectionGroup fallback

/**
 * Create a default team for OAuth user
 * This is called when user completes team setup in onboarding
 *
 * IDEMPOTENCY: This function checks if user already has a team before creating.
 * If user already has a team (via activeTeamId or membership), returns that team.
 * This prevents duplicate team creation from retries/double-clicks/race conditions.
 */
export async function createTeamForOAuthUser(
  user: User,
  companyName: string,
  phone: string = ''
): Promise<CreateTeamResult> {
  try {
    // ================================================================
    // AUTH READINESS GUARD
    // ================================================================
    const resolvedUser = await awaitAuthReady();
    if (!resolvedUser) {
      logger.warn('[EnsureBootstrap] Auth not ready — user is signed out, aborting team creation');
      return { success: false, teamId: null, error: 'AUTH_NOT_READY' };
    }
    logger.info('[BOOTSTRAP]', {
      uid: resolvedUser.uid,
      authReady: true,
      email: resolvedUser.email,
      phase: 'createTeamForOAuthUser',
    });

    // ================================================================
    // Step 0: Ensure /users/{uid} exists — Option B direct write.
    // setDoc with merge:true is idempotent: creates if absent, updates
    // only the specified fields if the doc already exists.
    // Strict allowlist: no isPlatformAdmin, no role (rules enforce this).
    // ================================================================
    logger.info('[BOOTSTRAP] Writing user doc (Option B) for user:', resolvedUser.uid);
    await setDoc(
      doc(db, 'users', user.uid),
      {
        uid: user.uid,
        email: user.email ?? '',
        displayName: user.displayName ?? null,
        photoURL: user.photoURL ?? null,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    const userDocCheck = await getDoc(doc(db, 'users', user.uid));

    // ================================================================
    // IDEMPOTENCY CHECK 1: Check if user already has activeTeamId
    // (read-only — update is allowed client-side)
    // userDocCheck is reused from the safety assertion above.
    // ================================================================
    const userRef = doc(db, 'users', user.uid);
    const userSnap = userDocCheck;

    if (userSnap.exists()) {
      const userData = userSnap.data();

      if (userData.activeTeamId) {
        const existingTeamSnap = await getDoc(doc(db, 'teams', userData.activeTeamId));
        if (existingTeamSnap.exists()) {
          logger.info('[EnsureBootstrap] User already has team, skipping creation:', {
            userId: user.uid,
            existingTeamId: userData.activeTeamId,
          });
          return { success: true, teamId: userData.activeTeamId, alreadyExisted: true };
        }
      }

      if (userData.initialTeamCreatedAt) {
        logger.warn(
          '[EnsureBootstrap] User has initialTeamCreatedAt but no valid activeTeamId, searching for team'
        );
        const existingTeamId = await findUserTeamMembership(user.uid);
        if (existingTeamId) {
          await setDoc(userRef, { activeTeamId: existingTeamId }, { merge: true });
          return { success: true, teamId: existingTeamId, alreadyExisted: true };
        }
      }
    }

    // ================================================================
    // IDEMPOTENCY CHECK 2: Check for any existing team membership
    // ================================================================
    const existingTeamId = await findUserTeamMembership(user.uid);
    if (existingTeamId) {
      logger.info('[EnsureBootstrap] Found existing team membership, skipping creation:', {
        userId: user.uid,
        existingTeamId,
      });
      await setDoc(
        userRef,
        { activeTeamId: existingTeamId, onboardingState: null, updatedAt: serverTimestamp() },
        { merge: true }
      );
      return { success: true, teamId: existingTeamId, alreadyExisted: true };
    }

    // ================================================================
    // No existing team found - create new team
    // ================================================================
    const teamId = doc(collection(db, 'teams')).id;
    const slug = companyName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .substring(0, 50);

    // ================================================================
    // Batch 1: Team doc + user update
    // Settings docs are in a SEPARATE batch (batch2) committed after
    // batch1, so that the settings create rule's get(teams/{teamId})
    // sees the already-committed team doc. If both were in the same
    // batch, get() would read pre-batch state (team doc absent) and
    // the bootstrap exception would evaluate to false → permission-denied.
    // ================================================================
    const batch1 = writeBatch(db);

    // Create team document
    const teamRef = doc(db, 'teams', teamId);
    batch1.set(teamRef, {
      id: teamId,
      name: companyName,
      slug,
      settings: {
        currency: 'EUR',
        locale: 'nl-NL',
        timezone: 'Europe/Amsterdam',
      },
      // Canonical entitlement object - SINGLE SOURCE OF TRUTH
      // Do NOT write legacy plan/planActivatedAt/planMeta fields
      entitlement: {
        tier: 'free',
        planId: 'free',
        accessStatus: 'free',
        activatedAt: serverTimestamp(),
        source: 'signup',
        schemaVersion: ENTITLEMENT_SCHEMA_VERSION,
      },
      entitlementSchemaVersion: ENTITLEMENT_SCHEMA_VERSION,
      phone,
      createdBy: user.email,
      createdByUid: user.uid,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    // Membership doc + index are written by bootstrapTeamOwnerMembershipCallable CF
    // after the batch commits (INV-MEMBER-1, INV-IDX-1 — Admin SDK only)

    // Update user profile with activeTeamId and safety flag
    batch1.set(
      userRef,
      {
        activeTeamId: teamId,
        onboardingState: null, // Clear the needs_team state
        // Safety flag to prevent duplicate team creation
        initialTeamId: teamId,
        initialTeamCreatedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    await batch1.commit();
    logger.info('[EnsureBootstrap] Batch 1 committed (team doc + user update)', {
      teamId,
      uid: user.uid,
    });

    // ================================================================
    // Batch 2: Settings docs
    // Committed AFTER batch1 so get(teams/{teamId}) in the settings
    // create rule resolves to the now-existing team doc, allowing the
    // createdByUid bootstrap exception to evaluate correctly.
    // ================================================================
    const batch2 = writeBatch(db);

    // Initialize company branding settings
    const brandingRef = doc(db, 'teams', teamId, 'settings', 'companyBranding');
    batch2.set(brandingRef, {
      companyName,
      phone,
      address: '',
      kvkNumber: '',
      vatNumber: '',
      logoUrl: '',
      updatedAt: serverTimestamp(),
    });

    // Initialize UI settings
    const uiRef = doc(db, 'teams', teamId, 'settings', 'ui');
    batch2.set(uiRef, {
      language: 'nl',
      dateFormat: 'DD-MM-YYYY',
      updatedAt: serverTimestamp(),
    });

    // Initialize onboarding checklist
    const onboardingRef = doc(db, 'teams', teamId, 'settings', 'onboarding');
    batch2.set(onboardingRef, {
      ...defaultTeamOnboarding,
      createdAt: serverTimestamp(),
    });

    await batch2.commit();
    logger.info('[EnsureBootstrap] Batch 2 committed (settings docs)', { teamId, uid: user.uid });

    // Bootstrap owner membership via CF (Admin SDK — INV-MEMBER-1, INV-IDX-1)
    const fns = getFunctions(undefined, 'europe-west1');
    const bootstrapMembership = httpsCallable<
      { teamId: string; displayName?: string; teamName?: string },
      { ok: boolean; created: boolean }
    >(fns, 'bootstrapTeamOwnerMembershipCallable');
    await bootstrapMembership({
      teamId,
      displayName: user.displayName || user.email?.split('@')[0] || 'User',
      teamName: companyName,
    });

    // CRITICAL: Force token refresh to pick up custom claims set by CF
    // The bootstrapMembership CF sets custom claims (activeTeamId, teamRole, etc.)
    // but the client's cached token doesn't have them yet. Without this refresh,
    // the user will get permission-denied errors when accessing team resources.
    try {
      await user.getIdToken(true);
      logger.info('[EnsureBootstrap] Token refreshed with custom claims', {
        uid: user.uid,
        teamId,
      });
    } catch (tokenErr) {
      logger.warn('[EnsureBootstrap] Token refresh failed (non-critical):', tokenErr);
    }

    // Belt-and-suspenders: also write the membership index client-side.
    // The CF writes this via Admin SDK but wraps it in a silent try/catch.
    // This client write guarantees the index is present even if the CF
    // index write fails silently. Idempotent (setDoc merge:true).
    await upsertMembershipIndex({
      uid: user.uid,
      teamId,
      teamRole: 'owner',
      active: true,
      teamName: companyName,
    });

    // INVESTIGATION: Verify token claims after membership bootstrap
    // This detects whether claims are present and correct after refreshUserClaims
    try {
      const tokenResult = await user.getIdTokenResult(true);
      const claims = tokenResult.claims;

      logger.info('[EnsureBootstrap] INVESTIGATION - Token claims after bootstrap', {
        uid: user.uid,
        teamId,
        claimsPresent: {
          claimsVersion: claims.claimsVersion ?? null,
          activeTeamId: claims.activeTeamId ?? null,
          teamRole: claims.teamRole ?? null,
          memberActive: claims.memberActive ?? null,
          teamStatus: claims.teamStatus ?? null,
        },
        claimsMatch: {
          activeTeamIdMatches: claims.activeTeamId === teamId,
          // eslint-disable-next-line no-restricted-syntax -- Token claim verification, not UI authorization
          teamRoleIsOwner: claims.teamRole === 'owner',
          memberActiveIsTrue: claims.memberActive === true,
          teamStatusIsActive: claims.teamStatus === 'active',
        },
        // CRITICAL: Detect partial claims bug
        /* eslint-disable no-restricted-syntax -- Token claim verification, not UI authorization */
        partialClaimsBug:
          claims.claimsVersion != null &&
          (claims.activeTeamId !== teamId ||
            claims.teamRole !== 'owner' ||
            claims.memberActive !== true ||
            claims.teamStatus !== 'active'),
        /* eslint-enable no-restricted-syntax */
      });

      // Log warning if claims are missing or wrong
      if (!claims.claimsVersion) {
        logger.warn('[EnsureBootstrap] No claimsVersion in token - fallback will be used', {
          uid: user.uid,
        });
      } else if (claims.activeTeamId !== teamId) {
        logger.error('[EnsureBootstrap] CRITICAL - activeTeamId mismatch in token', {
          uid: user.uid,
          expected: teamId,
          actual: claims.activeTeamId,
        });
      }
    } catch (tokenError) {
      logger.error('[EnsureBootstrap] Failed to verify token claims (non-critical)', {
        uid: user.uid,
        error: tokenError,
      });
    }

    logger.info('[EnsureBootstrap] Created team for OAuth user:', {
      userId: user.uid,
      teamId,
      companyName,
    });

    return { success: true, teamId, alreadyExisted: false };
  } catch (error) {
    logger.error('[EnsureBootstrap] Failed to create team for OAuth user:', error);
    return {
      success: false,
      teamId: null,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Main bootstrap function - ensures user has profile and checks for team
 *
 * This is idempotent and safe to call on every login.
 * Returns the user's state so the app can route appropriately.
 */
export async function ensureUserBootstrap(user: User): Promise<BootstrapResult> {
  try {
    // ================================================================
    // AUTH READINESS GUARD
    // ================================================================
    const resolvedUser = await awaitAuthReady();
    logger.info('[BOOTSTRAP]', {
      uid: user.uid,
      authReady: !!resolvedUser,
      resolvedUid: resolvedUser?.uid ?? null,
      phase: 'ensureUserBootstrap',
    });
    if (!resolvedUser) {
      logger.warn('[EnsureBootstrap] Auth not ready, aborting bootstrap');
      return {
        success: false,
        hasTeam: false,
        teamId: null,
        needsTeamSetup: true,
        error: 'AUTH_NOT_READY',
      };
    }

    // ================================================================
    // Step 1: Ensure /users/{uid} exists — Option B direct write.
    // setDoc with merge:true is idempotent: creates on first login,
    // refreshes profile fields (displayName, photoURL) on subsequent logins.
    // ================================================================
    logger.info('[EnsureBootstrap] Writing user doc (Option B) for user:', user.uid);
    await setDoc(
      doc(db, 'users', user.uid),
      {
        uid: user.uid,
        email: user.email ?? '',
        displayName: user.displayName ?? null,
        photoURL: user.photoURL ?? null,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    // ================================================================
    // Step 2: Check user doc for activeTeamId (may have been set by a prior session)
    // ================================================================
    const userSnap = await getDoc(doc(db, 'users', user.uid));
    if (userSnap.exists()) {
      const userData = userSnap.data();
      if (userData.activeTeamId) {
        const teamSnap = await getDoc(doc(db, 'teams', userData.activeTeamId));
        if (teamSnap.exists()) {
          logger.info('[EnsureBootstrap] User has valid activeTeamId:', userData.activeTeamId);
          return {
            success: true,
            hasTeam: true,
            teamId: userData.activeTeamId,
            needsTeamSetup: false,
          };
        }
        // Team doc gone — clear stale activeTeamId (update is allowed)
        logger.warn('[EnsureBootstrap] activeTeamId points to non-existent team, clearing...');
        await setDoc(
          doc(db, 'users', user.uid),
          { activeTeamId: null, updatedAt: serverTimestamp() },
          { merge: true }
        );
      }
    }

    // ================================================================
    // Step 3: Fallback membership lookup (read-only collectionGroup)
    // ================================================================
    const existingTeamId = await findUserTeamMembership(user.uid);
    if (existingTeamId) {
      logger.info('[EnsureBootstrap] Found existing team membership:', existingTeamId);
      await setDoc(
        doc(db, 'users', user.uid),
        { activeTeamId: existingTeamId, onboardingState: null, updatedAt: serverTimestamp() },
        { merge: true }
      );
      return { success: true, hasTeam: true, teamId: existingTeamId, needsTeamSetup: false };
    }

    // ================================================================
    // Step 4: No team found — route to create-team page
    // ================================================================
    logger.info('[EnsureBootstrap] User has no team, needs setup:', user.uid);
    return { success: true, hasTeam: false, teamId: null, needsTeamSetup: true };
  } catch (error) {
    logger.error('[EnsureBootstrap] Bootstrap failed:', error);
    return {
      success: false,
      hasTeam: false,
      teamId: null,
      needsTeamSetup: true,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}
